import { useState, useEffect, useMemo } from 'react';
import { collection, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import AppLayout from '../components/AppLayout';
import { HeaderSearch, ChoiceButton } from '../components/HeaderControls';
import { Button, ConfirmModal } from '../components/ui';
import { AddReminderModal, REMINDER_TYPE_MAP, fmtTime } from '../components/ReminderModal';
import type { Reminder } from '../types';

// Local calendar day (not UTC), see CLAUDE.md "Date-only strings".
function todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

type DateTab = 'upcoming' | 'past' | 'all';

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> reminder{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconPlus() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
}
function IconEdit() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>;
}
function IconTrash() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M2 4h10M5.5 4V2.5h3V4M3.5 4l.6 8.2c.05.7.6 1.3 1.3 1.3h3.2c.7 0 1.25-.6 1.3-1.3L10.5 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconBell({ size = 32 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
    <path d="M11 3c-3 0-5 2.2-5 5.5v3L4.5 14.5h13L16 11.5v-3C16 5.2 14 3 11 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M9 17.5a2 2 0 004 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RemindersPage() {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading,   setLoading]   = useState(true);

  const [dateTab,  setDateTab]  = useState<DateTab>('upcoming');
  const [searchQ,  setSearchQ]  = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState<Reminder | null>(null);
  const [deleting,  setDeleting]  = useState<Reminder | null>(null);
  const [deleteErr, setDeleteErr] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      collection(db, 'reminders'),
      snap => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() } as Reminder));
        rows.sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
        setReminders(rows);
        setLoading(false);
      },
      err => { console.error('Reminders listener error:', err); setLoading(false); },
    );
    return unsub;
  }, []);

  const today = todayISO();

  const upcomingCount = useMemo(() => reminders.filter(r => r.date >= today).length, [reminders, today]);
  const pastCount     = useMemo(() => reminders.filter(r => r.date <  today).length, [reminders, today]);

  const visible = useMemo(() => {
    let rows = reminders;
    if (dateTab === 'upcoming') rows = rows.filter(r => r.date >= today);
    else if (dateTab === 'past') rows = rows.filter(r => r.date < today);
    const q = searchQ.trim().toLowerCase();
    if (q) rows = rows.filter(r =>
      r.title.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q)
    );
    return rows;
  }, [reminders, dateTab, searchQ, today]);

  useEffect(() => { setCurrentPage(1); }, [dateTab, searchQ]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pagedVisible = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function openAdd()  { setEditing(null); setModalOpen(true); }
  function openEdit(r: Reminder) { setEditing(r); setModalOpen(true); }
  function closeModal() { setModalOpen(false); setEditing(null); }

  async function confirmDelete() {
    if (!deleting) return;
    const db = getClientDb();
    if (!db) return;
    setDeleteErr('');
    try {
      await deleteDoc(doc(db, 'reminders', deleting.id));
      setDeleting(null);
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : 'Failed to delete reminder.');
    }
  }

  function fmtDate(d: string) {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <AppLayout
      title="Reminders"
      subtitle="All follow-ups, past and upcoming"
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Search title, notes…" label="Search reminders" />
        <ChoiceButton<DateTab>
          label="Show" value={dateTab} onChange={setDateTab} defaultKey="upcoming"
          options={[
            { key: 'upcoming', label: 'Upcoming', count: upcomingCount },
            { key: 'past',     label: 'Past',     count: pastCount },
            { key: 'all',      label: 'All',      count: reminders.length },
          ]}
        />
        {(searchQ || dateTab !== 'upcoming') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateTab('upcoming'); }}>Clear</button>
        )}
      </>}
      headerActions={
        <Button variant="primary" size="sm" icon={<IconPlus />} onClick={openAdd} style={{ height: 34, boxSizing: 'border-box' }}>Add Reminder</Button>
      }
    >
      {/* ── List ── */}
      {loading ? (
        <div className="table-wrap">
          <table><thead><tr>{[90,80,120,200,90,70].map((w,i) => (
            <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
          ))}</tr></thead></table>
        </div>
      ) : visible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ marginBottom: 12, color: 'var(--text-3)', display: 'flex', justifyContent: 'center' }}><IconBell /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No reminders found</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            {reminders.length === 0 ? 'Add one to get started.' : 'No reminders match this filter.'}
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Time</th><th>Type</th><th>Title</th><th>Description</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedVisible.map(r => {
                const t = REMINDER_TYPE_MAP[r.type ?? 'other'];
                const overdue = r.date < today;
                return (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
                    <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: overdue ? 'var(--danger)' : 'var(--text-1)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {fmtDate(r.date)}
                    </td>
                    <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{fmtTime(r.time)}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 8px', borderRadius: 100, fontSize: 11, fontWeight: 600,
                        background: t.bg, color: t.color,
                      }}>
                        {t.icon}{t.label}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{r.title}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.description || '—'}
                    </td>
                    <td className="col-actions">
                      <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                        <button
                          title="Edit" onClick={() => openEdit(r)}
                          style={{ padding: '5px 8px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer', display: 'flex' }}
                        ><IconEdit /></button>
                        <button
                          title="Delete" onClick={() => { setDeleting(r); setDeleteErr(''); }}
                          style={{ padding: '5px 8px', border: '1.5px solid color-mix(in srgb, var(--danger) 35%, transparent)', borderRadius: 'var(--r-sm)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', display: 'flex' }}
                        ><IconTrash /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={visible.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      <AddReminderModal open={modalOpen} onClose={closeModal} editing={editing} />

      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete Reminder"
        message={deleting ? <>Delete reminder <strong>{deleting.title}</strong>?</> : null}
        confirmLabel="Delete"
        error={deleteErr}
      />
    </AppLayout>
  );
}
